import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-4081">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4082">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-4083">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-4084">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-4085" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-4086">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4087" />
              <p className="text-sm text-gray-600" data-id="element-4088">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-4089">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4090" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-4091">
          {/* Quality & Metadata */}
          <div data-id="element-4092">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4093">
              <Brain className="w-3.5 h-3.5" data-id="element-4094" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-4095">
              <div className="grid grid-cols-2 gap-4" data-id="element-4096">
                <div data-id="element-4097">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4098">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4099">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-4100">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4101">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-4102">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-4103">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4104">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div className="col-span-2" data-id="element-4105">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4106">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4107">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-4108">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4109">
              <Database className="w-3.5 h-3.5" data-id="element-4110" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-4111">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4112" />
                <span className="text-sm text-gray-600" data-id="element-4113">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-4114">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-4115">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-4116">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4117">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-4118" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-4119">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-4120">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4121" />
                  <span className="text-sm" data-id="element-4122">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-4123">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4124" />
                  <span className="text-sm" data-id="element-4125">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-4126">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4127">
              <BookOpen className="w-3.5 h-3.5" data-id="element-4128" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-4129">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4130">
                      <div data-id="element-4131">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4132">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4133">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DOK_MISSING') ? <span className="text-amber-600" data-id="element-4134">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-4135">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4136">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4137">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-4138">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_MISCONCEPTION_MISSING') ? <span className="text-amber-500" data-id="element-4139">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-4140">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-4141">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4142" />
                        <span className="text-sm text-gray-600" data-id="element-4143">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-4144">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-4145" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4146" />}
                        <span className="text-sm text-gray-600" data-id="element-4147">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}